import { logger } from '@/utils/logger';
/* eslint-disable @typescript-eslint/no-explicit-any */
import { confirmResetPassword, logout } from "@/services/auth/emailAuthService";
import { changePassword } from "@/services/auth/userService";
import { sanitizeInput } from "@/utils/xssProtection";
import { useEffect, useState } from "react";
import styled from "styled-components";
import { GradientButton } from "../button/Button";
import { useSnackbar } from "../common/Snackbar";
import { PasswordConfirmInput, PasswordInput } from "../input/Inputs";
import { Modal } from "../modal/Modal";

interface PWChangeModalProps {
  isOpen: boolean;
  onClose: () => void;
  email: string;
  uid?: string;
  token?: string;
  onSuccess?: () => void;
  isResetMode?: boolean;
}

const PWChangeModal: React.FC<PWChangeModalProps> = ({   
  isOpen, 
  onClose, 
  email, 
  uid,
  token,
  onSuccess,
  isResetMode = false 
}) => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { showSnackbar } = useSnackbar();
  
  // 비밀번호 유효성 검사
  const validatePassword = (password: string): boolean => {
    if (password.length < 8) {
      setError('Password must be at least 8 characters long');
      return false;
    }
    setError('');
    return true;
  };

  const handleChangePassword = async () => {
    if (!validatePassword(newPassword) || newPassword !== confirmPassword) {
      setError('Passwords do not match or are invalid');
      return;
    }

    try {
      setIsLoading(true);
      setError('');
      
      if (isResetMode && uid && token) {
        // 비밀번호 재설정 API 호출 (sanitize input before sending)
        const result = await confirmResetPassword(
          uid,
          token,
          sanitizeInput(newPassword),
          sanitizeInput(confirmPassword)
        );
        if (result._meta.status === 200) {
          showSnackbar('Password has been reset successfully. Please log in.', { showCheckIcon: true });
          setError('');
          onSuccess?.();
        } else {
          setError('Failed to reset password');
        }
      } else {
        // 일반 비밀번호 변경 로직 (로그인된 상태, sanitize input before sending)
        const response = await changePassword(sanitizeInput(newPassword), sanitizeInput(confirmPassword));
        const isSuccess = response._meta.status === 200 || response._meta.status === 201;
        if (isSuccess) {
          showSnackbar('Password changed successfully', { showCheckIcon: true });
          setError('');
          onSuccess?.();
          // 로그아웃 시키기
          await logout();
        } else {
          setError('Failed to change password');
        }
      }
    } catch (error: any) {
      logger.error('PWChangeModal - Password change error:', error);
      
      // 에러 메시지 처리
      if (error.response?.data?.detail) {
        setError(error.response.data.detail);
      } else if (error.message) {
        setError(error.message);
      } else {
        setError('비밀번호 변경 중 오류가 발생했습니다.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // 모달 닫힐 때 state 초기화
  useEffect(() => {
    if (!isOpen) {
      setNewPassword('');
      setConfirmPassword('');
      setError('');
    }
  }, [isOpen]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={isResetMode ? "Reset Password" : "Change Password"}  size="medium" >
        <ModalContent>
            <SubText>{email || 'your@email.com'}</SubText>
              <InputWrapper 
                onSubmit={(e) => {
                  e.preventDefault();
                  handleChangePassword();
              }}>
                  <PasswordInput
                      name="newPassword"
                      label="New Password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      onBlur={() => validatePassword(newPassword)}
                      error={error}
                      placeholder="Up to 18 characters. Required: letters, numbers, and symbols"
                      showPassword
                      onTogglePassword={() => {}}
                  />
                  <PasswordConfirmInput
                      name="confirmPassword"
                      label="Confirm Password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      onBlur={() => validatePassword(newPassword)}
                      error={error}
                      originalPassword={newPassword}
                  />
              </InputWrapper>
              <ButtonGroup>
                  <GradientButton
                      onClick={handleChangePassword}
                      loading={isLoading}
                      width="100%"
                      height="47px"
                  >
                      Reset Password
                  </GradientButton>
              </ButtonGroup>
        </ModalContent>
    </Modal>
  );
};

export default PWChangeModal;

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  box-sizing: border-box;
`;

const SubText = styled.p`
  color: ${({ theme }) => theme.colors.fontGray[400]};
  font-size: 20px;
  font-weight: 400;
  line-height: normal;
  margin-bottom: 32px;
`;

const InputWrapper = styled.form`
  width: 100%;
  max-width: 533px;
  display: flex;
  flex-direction: column;
  gap: 32px;
  margin-bottom: 48px;
`;

const ButtonGroup = styled.div`
  width: 100%;
  max-width: 533px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 72px;
`;